import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import PlanCard from "@/components/PlanCard";
import { fetchPlans, FALLBACK_PLANS } from "@/lib/plans";
import useScrollReveal from "@/hooks/useScrollReveal";
import HeroBadge from "@/components/HeroBadge";
import { Phone, ArrowRight, Shield, Clock3, Users, ChevronUp } from "lucide-react";

// Bronze · Silver · Gold theme — mirrors PlanCard.jsx for visual consistency.
const TIER_THEME = {
  bronze:  { grad: "from-[#C28B6F] via-[#A8704F] to-[#8B5A3C]", tint: "bg-[#B8826E]/8",  border: "border-[#B8826E]/40", text: "text-[#8B5A3C]", btn: "from-[#B8826E] to-[#8B5A3C]" },
  silver:  { grad: "from-[#B6C2CC] via-[#8A98A4] to-[#5E6B78]", tint: "bg-[#8A98A4]/10", border: "border-[#8A98A4]/40", text: "text-[#5E6B78]", btn: "from-[#8A98A4] to-[#5E6B78]" },
  gold:    { grad: "from-[#E3BC58] via-[#C89B3C] to-[#9C7421]", tint: "bg-[#C89B3C]/12", border: "border-[#C89B3C]/45", text: "text-[#9C7421]", btn: "from-[#C89B3C] to-[#9C7421]" },
};

export default function Plans() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const compareRef = useRef(null);
  useScrollReveal();

  useEffect(() => {
    let ok = true;
    fetchPlans().then((p) => { if (ok) setPlans(p); });
    return () => { ok = false; };
  }, []);

  // Sticky "Compare plans" CTA bar — visible while comparison section is in viewport.
  useEffect(() => {
    if (!compareRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowStickyBar(entry.isIntersecting),
      { root: null, threshold: 0, rootMargin: "-120px 0px -80px 0px" },
    );
    obs.observe(compareRef.current);
    return () => obs.disconnect();
  }, []);

  // Map plan id → tier (so sticky-bar Buy buttons link to real plan ids when API resolves).
  const planByTier = Object.fromEntries(plans.map((p) => [p.tier, p]));
  const bronze = planByTier.individual;
  const silver = planByTier.husband_wife;
  const gold   = planByTier.family;

  return (
    <SiteLayout>
      <section className="hero-grad relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-12 relative">
          <div className="max-w-3xl reveal">
            <HeroBadge>3 Plans · <span className="text-[#A53A2A]">From ₹2,000 / yr</span></HeroBadge>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0F2540] leading-[1.04]">
              Find the plan
              <br /> that fits <span className="text-[#A53A2A]">your life.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-[#5C6B7A]">
              Three tiers. One promise — effortless access to Nirmal Hospital’s specialists, thoughtfully designed healthcare membership, trusted support, and peace of mind — all under one roof with trusted healthcare.
            </p>
          </div>
        </div>
      </section>

      {/* Plan cards — Bronze / Silver / Gold themed */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="plans-grid">
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p, i) => <PlanCard key={p.plan_id} plan={p} index={i} />)}
        </div>
      </section>

      {/* Compare NPC tiers — Bronze / Silver / Gold themed table */}
      <section ref={compareRef} className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="compare-section" id="compare">
        <div className="reveal">
          <span className="overline">Side-by-side</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540]">Compare all three plans.</h2>
          <p className="mt-3 text-[#5C6B7A] max-w-2xl">A clear breakdown of what each NPC tier unlocks, so you can pick with confidence.</p>
        </div>

        {/* Comparison table — Desktop (md+) */}
        <div className="mt-10 reveal hidden md:block">
          <div className="rounded-2xl bg-white border border-[#E1E8F0] overflow-hidden shadow-[0_24px_60px_-30px_rgba(15,37,64,0.18)]">
            {/* Header row with Bronze / Silver / Gold tier blocks */}
            <div className="grid grid-cols-4 text-sm">
              <div className="p-4 lg:p-5 font-semibold tracking-wide bg-gradient-to-r from-[#0F2540] to-[#1E4F87] text-white">Benefit</div>

              <div className={`p-4 lg:p-5 text-center text-white bg-gradient-to-br ${TIER_THEME.bronze.grad}`} data-testid="compare-col-bronze">
                <div className="text-[10px] uppercase tracking-[0.22em] opacity-90 font-semibold">Individual</div>
                <div className="font-display font-bold text-lg mt-1">Bronze</div>
                <div className="text-[11px] opacity-95 font-normal mt-1">₹2,000 · incl. GST</div>
              </div>

              <div className={`p-4 lg:p-5 text-center text-white bg-gradient-to-br ${TIER_THEME.silver.grad} relative`} data-testid="compare-col-silver">
                <div className="text-[10px] uppercase tracking-[0.22em] opacity-90 font-semibold">Husband Wife</div>
                <div className="font-display font-bold text-lg mt-1">Silver</div>
                <div className="text-[11px] opacity-95 font-normal mt-1">₹3,000 · incl. GST</div>
              </div>

              <div className={`p-4 lg:p-5 text-center text-white bg-gradient-to-br ${TIER_THEME.gold.grad}`} data-testid="compare-col-gold">
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-white/95 text-[#0F2540] text-[9px] font-bold tracking-wider">MOST LOVED</div>
                <div className="text-[10px] uppercase tracking-[0.22em] opacity-90 font-semibold">Family</div>
                <div className="font-display font-bold text-lg mt-1">Gold</div>
                <div className="text-[11px] opacity-95 font-normal mt-1">₹6,000 · incl. GST</div>
              </div>
            </div>

            {/* Rows */}
            {[
              ["OPD consultations", "Unlimited", "Unlimited", "Unlimited"],
              ["Lab tests discount", "20%", "20%", "25%"],
              ["Radiology discount", "20%", "20%", "25%"],
              ["Health Check-up discount", "20%", "20%", "25%"],
              ["Pharmacy discount", "10%", "10%", "15%"],
              ["Hospital billing discount", "10%", "10%", "10%"],
              ["Vaccination discount", "5%", "5%", "10%"],
              ["Family members covered", "1", "2 (Husband + Wife)", "Up to 6 — Husband + Wife + Children <18 + Parents"],
            ].map(([l, a, b, c], i) => (
              <div key={l} className={`grid grid-cols-4 text-sm ${i % 2 ? "bg-[#FAFBFC]" : "bg-white"}`}>
                <div className="p-4 lg:p-5 text-[#0F2540] font-medium border-r border-[#E1E8F0]/60">{l}</div>
                <div className={`p-4 lg:p-5 text-center text-[#0F2540] border-r border-[#E1E8F0]/60 ${TIER_THEME.bronze.tint}`}>{a}</div>
                <div className={`p-4 lg:p-5 text-center text-[#0F2540] border-r border-[#E1E8F0]/60 ${TIER_THEME.silver.tint}`}>{b}</div>
                <div className={`p-4 lg:p-5 text-center text-[#0F2540] font-semibold ${TIER_THEME.gold.tint}`}>{c}</div>
              </div>
            ))}

            {/* Buy footer row */}
            <div className="grid grid-cols-4 text-sm border-t border-[#E1E8F0]">
              <div className="p-4 lg:p-5 text-[11px] uppercase tracking-[0.18em] text-[#5C6B7A] font-semibold bg-[#FAFBFC] flex items-center">Get started</div>
              {[
                { tier: "bronze", plan: bronze },
                { tier: "silver", plan: silver },
                { tier: "gold",   plan: gold },
              ].map(({ tier, plan }) => (
                <div key={tier} className={`p-4 lg:p-5 text-center ${TIER_THEME[tier].tint}`}>
                  <Link
                    to={plan ? `/join?plan=${plan.plan_id}` : "/join"}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-semibold bg-gradient-to-r ${TIER_THEME[tier].btn} hover:opacity-90 transition shadow-md`}
                    data-testid={`compare-buy-${tier}-btn`}
                  >
                    Buy {tier[0].toUpperCase() + tier.slice(1)} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison — Mobile stacked cards (<md) */}
        <div className="mt-8 reveal md:hidden space-y-5" data-testid="compare-mobile">
          {[
            { tier: "bronze", label: "Bronze", sub: "Individual", price: "₹2,000", plan: bronze, rows: [["OPD consultations","Unlimited"],["Lab tests","20%"],["Radiology","20%"],["Health Check-up","20%"],["Pharmacy","10%"],["Hospital billing","10%"],["Vaccination","5%"],["Members covered","1"]] },
            { tier: "silver", label: "Silver", sub: "Husband Wife", price: "₹3,000", plan: silver, mostLoved: true, rows: [["OPD consultations","Unlimited"],["Lab tests","20%"],["Radiology","20%"],["Health Check-up","20%"],["Pharmacy","10%"],["Hospital billing","10%"],["Vaccination","5%"],["Members covered","2 (Husband + Wife)"]] },
            { tier: "gold",   label: "Gold",   sub: "Family",       price: "₹6,000", plan: gold,   rows: [["OPD consultations","Unlimited"],["Lab tests","25%"],["Radiology","25%"],["Health Check-up","25%"],["Pharmacy","15%"],["Hospital billing","10%"],["Vaccination","10%"],["Members covered","Up to 6 — H + W + Children + Parents"]] },
          ].map(({ tier, label, sub, price, plan, mostLoved, rows }) => (
            <div key={tier} className="rounded-2xl overflow-hidden bg-white border border-[#E1E8F0] shadow-[0_14px_40px_-22px_rgba(15,37,64,0.22)]" data-testid={`compare-mcard-${tier}`}>
              <div className={`relative p-5 text-white bg-gradient-to-br ${TIER_THEME[tier].grad}`}>
                {mostLoved && <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-white/95 text-[#0F2540] text-[9px] font-bold tracking-wider">MOST LOVED</div>}
                <div className="text-[10px] uppercase tracking-[0.22em] opacity-90 font-semibold">{sub}</div>
                <div className="font-display font-bold text-2xl mt-1">{label}</div>
                <div className="text-[12px] opacity-95 mt-1">{price} · incl. 18% GST · 12 months</div>
              </div>
              <div className="divide-y divide-[#E1E8F0]">
                {rows.map(([k, v]) => (
                  <div key={k} className="px-5 py-3 flex items-center justify-between gap-3 text-[14px]">
                    <span className="text-[#5C6B7A]">{k}</span>
                    <span className="text-[#0F2540] font-semibold text-right">{v}</span>
                  </div>
                ))}
              </div>
              <div className="p-4 bg-[#FAFBFC]">
                <Link
                  to={plan ? `/join?plan=${plan.plan_id}` : "/join"}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white text-sm font-semibold bg-gradient-to-r ${TIER_THEME[tier].btn} hover:opacity-95 transition shadow-md`}
                  data-testid={`compare-mbuy-${tier}-btn`}
                >
                  Buy {label} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-5">
        {[
          [Shield, "100% secure payments", "Razorpay-secured checkout. Your data and card details never touch our servers."],
          [Clock3, "Instant activation", "Digital card with QR is issued the moment your payment succeeds."],
          [Users, "Family-first design", "Add family members anytime, free of cost, up to your plan limit."],
        ].map(([Icon, t, d], i) => (
          <div key={t} className="reveal lux-card rounded-2xl p-7" style={{ animationDelay: `${i * 100}ms` }}>
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#1E4F87] to-[#0F2540] text-white grid place-items-center"><Icon className="h-5 w-5" /></div>
            <h3 className="mt-5 font-display text-lg font-semibold text-[#0F2540]">{t}</h3>
            <p className="mt-2 text-sm text-[#5C6B7A]">{d}</p>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="relative rounded-[2rem] mesh-dark p-10 lg:p-14 overflow-hidden">
          <div className="absolute inset-0 dot-grid opacity-20 pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <span className="overline text-[#C89B3C]">Need help choosing?</span>
              <h3 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-white">Talk to an NPC advisor.</h3>
              <p className="mt-3 text-white/80 max-w-xl">An advisor can recommend the right plan for your family in under 5 minutes. No commitment, no spam.</p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
              <Link to="/contact" className="bg-white text-[#0F2540] px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-[#C89B3C] transition">Request callback <ArrowRight className="h-4 w-4" /></Link>
              <a href="tel:+916357033301" className="border border-white/40 text-white px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-white/10 transition"><Phone className="h-4 w-4" /> +91-63570-33301</a>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky "Compare plans" CTA bar — visible while comparison section is on-screen */}
      <div
        className={`fixed inset-x-0 bottom-0 z-40 px-3 pb-3 transition-all duration-300 ease-out ${showStickyBar ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"}`}
        data-testid="compare-sticky-bar"
      >
        <div className="mx-auto max-w-5xl rounded-2xl bg-white/95 backdrop-blur-md border border-[#E1E8F0] shadow-[0_18px_50px_-12px_rgba(15,37,64,0.25)] px-3 sm:px-5 py-3 flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:flex flex-col mr-2">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B7A] font-semibold">Compare plans</span>
            <span className="text-sm text-[#0F2540] font-semibold">Pick your tier →</span>
          </div>
          {[
            { tier: "bronze", label: "Bronze", price: "₹2,000", plan: bronze },
            { tier: "silver", label: "Silver", price: "₹3,000", plan: silver },
            { tier: "gold",   label: "Gold",   price: "₹6,000", plan: gold   },
          ].map(({ tier, label, price, plan }) => (
            <Link
              key={tier}
              to={plan ? `/join?plan=${plan.plan_id}` : "/join"}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-white text-xs sm:text-sm font-semibold bg-gradient-to-r ${TIER_THEME[tier].btn} hover:opacity-95 transition shadow-md`}
              data-testid={`sticky-buy-${tier}-btn`}
            >
              <span>{label}</span>
              <span className="opacity-85 font-normal hidden sm:inline">· {price}</span>
              <ArrowRight className="h-3.5 w-3.5 hidden sm:inline" />
            </Link>
          ))}
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="ml-1 h-10 w-10 grid place-items-center rounded-xl border border-[#E1E8F0] text-[#0F2540] hover:bg-[#F8FAFC] transition"
            aria-label="Back to top"
            data-testid="sticky-top-btn"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
        </div>
      </div>
    </SiteLayout>
  );
}
